/**
 * CatalogoServicios — Página de gestión del catálogo de servicios y precios.
 *
 * Acceso desde Dashboard → tarjeta "Catálogo".
 * CRUD completo: crear, editar, desactivar servicios.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Paper, Chip, CircularProgress,
  Dialog, Slide, TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  Tooltip, Skeleton, InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const API_URL = '/api';
const userId = localStorage.getItem('user_id');

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
};

const CATEGORIA_OPCIONES = [
  { id: 'consulta',      label: 'Consulta',     color: '#1e3a5f', bg: '#eff6ff' },
  { id: 'procedimiento', label: 'Procedimiento',color: '#7c3aed', bg: '#ede9fe' },
  { id: 'estudio',       label: 'Estudio',      color: '#0891b2', bg: '#ecfeff' },
  { id: 'otro',          label: 'Otro',         color: '#64748b', bg: '#f1f5f9' },
];

function fmtMoney(num, moneda = 'MXN') {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(Number(num || 0));
}

export default function CatalogoServicios() {
  const navigate = useNavigate();
  const [servicios, setServicios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('todos');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/emr/finanzas/servicios?activo=true`, {
        headers: { 'X-User-Id': userId },
      });
      if (res.ok) setServicios(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleEliminar = async (s) => {
    if (!window.confirm(`¿Desactivar servicio "${s.nombre}"?\n\nNo se borra del histórico, solo deja de aparecer en el selector.`)) return;
    try {
      const res = await fetch(`${API_URL}/emr/finanzas/servicios/${s.id}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': userId },
      });
      if (!res.ok) throw new Error(await res.text());
      cargar();
    } catch (e) {
      alert('No se pudo eliminar: ' + e.message);
    }
  };

  // Filtrar
  const filtrados = useMemo(() => {
    let arr = [...servicios];
    if (filtroCategoria !== 'todos') {
      arr = arr.filter(s => s.categoria === filtroCategoria);
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      arr = arr.filter(s =>
        (s.nombre || '').toLowerCase().includes(q) ||
        (s.codigo || '').toLowerCase().includes(q) ||
        (s.descripcion || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [servicios, busqueda, filtroCategoria]);

  // Conteos por categoría (sobre todos los servicios, ignorando filtro)
  const conteos = useMemo(() => {
    const c = { todos: servicios.length };
    for (const cat of CATEGORIA_OPCIONES) {
      c[cat.id] = servicios.filter(s => s.categoria === cat.id).length;
    }
    return c;
  }, [servicios]);

  // Agrupar visualmente por categoría
  const porCategoria = useMemo(() => {
    return filtrados.reduce((acc, s) => {
      if (!acc[s.categoria]) acc[s.categoria] = [];
      acc[s.categoria].push(s);
      return acc;
    }, {});
  }, [filtrados]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* Header */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#64748b' }}>
            <ArrowBackIcon />
          </IconButton>
          <ReceiptIcon sx={{ fontSize: 32, color: '#7c3aed' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: { xs: 18, sm: 22 } }}>
              Catálogo de servicios
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              {servicios.length} servicios activos · usados al cobrar consultas y procedimientos
            </Typography>
          </Box>
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => setEditando('nuevo')}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 13, borderRadius: 2, px: 2.5, py: 0.9,
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' },
            }}
          >
            Nuevo servicio
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, pb: 6, maxWidth: 1200, mx: 'auto' }}>

        {/* Búsqueda + filtros */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Buscar por nombre, código o descripción..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5, background: '#fff', fontSize: 14,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&.Mui-focused fieldset': { borderColor: '#7c3aed', borderWidth: 2 },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
              endAdornment: busqueda && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setBusqueda('')}>
                    <CloseIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Chips de categoría */}
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
            <CategoriaChip
              activa={filtroCategoria === 'todos'}
              label="Todos"
              count={conteos.todos}
              color="#1e3a5f"
              onClick={() => setFiltroCategoria('todos')}
            />
            {CATEGORIA_OPCIONES.map(c => (
              <CategoriaChip
                key={c.id}
                activa={filtroCategoria === c.id}
                label={c.label}
                count={conteos[c.id] || 0}
                color={c.color}
                onClick={() => setFiltroCategoria(c.id)}
              />
            ))}
          </Box>
        </Box>

        {/* Lista */}
        {cargando ? (
          <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />
        ) : filtrados.length === 0 ? (
          <Paper sx={{
            p: 6, textAlign: 'center', borderRadius: 3,
            border: '1px dashed #cbd5e1', background: '#fafbfc',
          }}>
            <ReceiptIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>
              {busqueda
                ? `Sin resultados para "${busqueda}"`
                : filtroCategoria !== 'todos'
                  ? `Sin servicios en la categoría "${CATEGORIA_OPCIONES.find(c => c.id === filtroCategoria)?.label}"`
                  : 'Sin servicios en el catálogo'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
              {!busqueda && filtroCategoria === 'todos' && 'Usa "Nuevo servicio" para empezar'}
            </Typography>
          </Paper>
        ) : (
          Object.entries(porCategoria).map(([cat, items]) => {
            const catCfg = CATEGORIA_OPCIONES.find(c => c.id === cat) || { label: cat, color: '#64748b', bg: '#f1f5f9' };
            return (
              <Box key={cat} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.6, ml: 0.5 }}>
                  <Box sx={{ width: 4, height: 14, background: catCfg.color, borderRadius: 1 }} />
                  <Typography sx={{
                    fontSize: 11, fontWeight: 800, color: catCfg.color,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>
                    {catCfg.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                    · {items.length}
                  </Typography>
                </Box>
                <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  {items.map((s, i) => (
                    <ServicioFila
                      key={s.id} servicio={s} catCfg={catCfg}
                      esUltimo={i === items.length - 1}
                      onEditar={() => setEditando(s)}
                      onEliminar={() => handleEliminar(s)}
                    />
                  ))}
                </Paper>
              </Box>
            );
          })
        )}
      </Box>

      {editando && (
        <DialogServicio
          servicio={editando === 'nuevo' ? null : editando}
          onClose={() => setEditando(null)}
          onGuardado={() => { setEditando(null); cargar(); }}
        />
      )}
    </Box>
  );
}

// ============================================================================
function CategoriaChip({ activa, label, count, color, onClick }) {
  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <span>{label}</span>
          <Box sx={{
            fontSize: 10, fontWeight: 700,
            bgcolor: activa ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
            color: activa ? '#fff' : '#475569',
            px: 0.7, borderRadius: 1,
          }}>
            {count}
          </Box>
        </Box>
      }
      onClick={onClick}
      sx={{
        fontSize: 12.5, fontWeight: 600, height: 30, cursor: 'pointer',
        color: activa ? '#fff' : '#475569',
        background: activa ? color : '#fff',
        border: activa ? 'none' : '1px solid #e2e8f0',
        '&:hover': { background: activa ? color : '#f8fafc' },
      }}
    />
  );
}

function ServicioFila({ servicio, catCfg, esUltimo, onEditar, onEliminar }) {
  return (
    <Box sx={{
      p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
      borderBottom: esUltimo ? 'none' : '1px solid #f1f5f9',
      transition: 'background 0.15s',
      '&:hover': { background: '#fafbfc', '& .row-actions': { opacity: 1 } },
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
        background: catCfg.bg, color: catCfg.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b' }}>
            {servicio.nombre}
          </Typography>
          {servicio.codigo && (
            <Chip label={servicio.codigo} size="small" sx={{
              height: 18, fontSize: 10, fontWeight: 700,
              background: '#f1f5f9', color: '#64748b',
              fontFamily: 'monospace',
            }} />
          )}
          {servicio.duracion_minutos && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#94a3b8' }}>
              <ScheduleIcon sx={{ fontSize: 12 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{servicio.duracion_minutos} min</Typography>
            </Box>
          )}
        </Box>
        {servicio.descripcion && (
          <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 500 }}>
            {servicio.descripcion}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: catCfg.color, mr: 1, flexShrink: 0 }}>
        {fmtMoney(servicio.precio, servicio.moneda)}
      </Typography>
      <Box className="row-actions" sx={{ display: 'flex', gap: 0.3, opacity: 0, transition: 'opacity 0.15s' }}>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={onEditar} sx={{ color: '#6366f1', '&:hover': { background: '#eef2ff' } }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Desactivar">
          <IconButton size="small" onClick={onEliminar} sx={{ color: '#dc2626', '&:hover': { background: '#fef2f2' } }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ============================================================================
function DialogServicio({ servicio, onClose, onGuardado }) {
  const esEdicion = Boolean(servicio);
  const [datos, setDatos] = useState({
    nombre: servicio?.nombre || '',
    codigo: servicio?.codigo || '',
    descripcion: servicio?.descripcion || '',
    precio: servicio?.precio ? String(servicio.precio) : '',
    moneda: servicio?.moneda || 'MXN',
    categoria: servicio?.categoria || 'consulta',
    duracion_minutos: servicio?.duracion_minutos ? String(servicio.duracion_minutos) : '',
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const valido = datos.nombre.trim().length >= 2 && parseFloat(datos.precio) >= 0;

  const handleGuardar = async () => {
    setGuardando(true);
    setError('');
    try {
      const body = {
        nombre: datos.nombre.trim(),
        codigo: datos.codigo.trim() || null,
        descripcion: datos.descripcion.trim() || null,
        precio: parseFloat(datos.precio),
        moneda: datos.moneda,
        categoria: datos.categoria,
        duracion_minutos: datos.duracion_minutos ? parseInt(datos.duracion_minutos) : null,
      };
      const url = esEdicion
        ? `${API_URL}/emr/finanzas/servicios/${servicio.id}`
        : `${API_URL}/emr/finanzas/servicios`;
      const res = await fetch(url, {
        method: esEdicion ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt.substring(0, 200));
      }
      onGuardado?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open onClose={() => !guardando && onClose()}
      TransitionComponent={SlideUp}
      maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: '#fff',
      }}>
        <ReceiptIcon sx={{ fontSize: 18, color: '#fef3c7' }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>
          {esEdicion ? 'Editar servicio' : 'Nuevo servicio'}
        </Typography>
        <IconButton size="small" onClick={onClose} disabled={guardando}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4 }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          sx={inputStyle} size="small"
          label="Nombre del servicio" required autoFocus
          value={datos.nombre}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />

        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            label="Código (opcional)" placeholder="CONS-GEN"
            value={datos.codigo}
            onChange={(e) => setDatos({ ...datos, codigo: e.target.value.toUpperCase() })}
          />
          <FormControl size="small" sx={{ ...inputStyle, flex: 1 }}>
            <InputLabel shrink>Categoría</InputLabel>
            <Select
              label="Categoría" notched
              value={datos.categoria}
              onChange={(e) => setDatos({ ...datos, categoria: e.target.value })}
            >
              {CATEGORIA_OPCIONES.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="number" label="Precio" required
            value={datos.precio}
            onChange={(e) => setDatos({ ...datos, precio: e.target.value })}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <FormControl size="small" sx={{ ...inputStyle, minWidth: 90 }}>
            <InputLabel shrink>Moneda</InputLabel>
            <Select label="Moneda" notched value={datos.moneda} onChange={(e) => setDatos({ ...datos, moneda: e.target.value })}>
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="COP">COP</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="number" label="Duración (min)"
            value={datos.duracion_minutos}
            onChange={(e) => setDatos({ ...datos, duracion_minutos: e.target.value })}
            inputProps={{ min: 0, max: 600 }}
          />
        </Box>

        <TextField
          sx={inputStyle} size="small" multiline minRows={2} maxRows={3}
          label="Descripción (opcional)"
          value={datos.descripcion}
          onChange={(e) => setDatos({ ...datos, descripcion: e.target.value })}
        />

        {error && <Alert severity="error" sx={{ py: 0.4, fontSize: 12 }}>{error}</Alert>}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
        <Button
          fullWidth onClick={onClose} disabled={guardando}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: 2, py: 1,
            color: '#475569', background: '#f1f5f9',
            '&:hover': { background: '#e2e8f0' },
          }}
        >
          Cancelar
        </Button>
        <Button
          fullWidth variant="contained"
          onClick={handleGuardar} disabled={!valido || guardando}
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 2, py: 1,
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {guardando ? 'Guardando...' : (esEdicion ? 'Guardar' : 'Crear')}
        </Button>
      </Box>
    </Dialog>
  );
}
